import { apiClient, ApiResponse } from './apiClient';

// Category interfaces matching API structure
export interface CategoryProduct {
  id: string;
  name: string;
  status: string;
  stock: number;
}

export interface Category {
  id: number;
  name: string;
  name_ar: string;
  icon: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  Products?: CategoryProduct[];
}

export interface CategoryDetails extends Category {
  Products: CategoryProduct[];
}

export interface CreateCategoryRequest {
  name: string;
  name_ar: string;
  icon?: string;
  is_active?: boolean;
}

export interface UpdateCategoryRequest {
  name?: string;
  name_ar?: string;
  icon?: string;
  is_active?: boolean;
}

export interface CategoryFilters {
  page?: number; // Required, default: 1
  limit?: number; // Required, default: 50, max: 100
  is_active?: string; // Optional: Filter by active status (true/false)
  q?: string; // Optional: Search by name or name_ar
}

export interface CategoryPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CategoriesResponse {
  items: Category[];
  pagination: CategoryPagination;
}

export interface CategoryListResponse {
  items: Category[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export class CategoryService {
  private baseUrl = '/admin/categories';

  // Get categories with filters (GET /admin/categories)
  async getCategories(filters: CategoryFilters = {}): Promise<ApiResponse<CategoryListResponse>> {
    const params = new URLSearchParams();
    
    // Required params
    const page = filters.page || 1;
    const limit = Math.min(filters.limit || 50, 100); // Max 100 per API spec
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Optional filters
    if (filters.q) {
      params.append('q', filters.q);
    }
    if (filters.is_active !== undefined && filters.is_active !== 'all') {
      params.append('is_active', filters.is_active);
    }
    
    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    try {
      // API returns: {status: boolean, message: string, data: {items: [], pagination: {}}}
      // apiClient.get returns: {success: boolean, data: {...}, message: string}
      const response = await apiClient.get<any>(url);

      // Handle API response structure: {status, message, data: {items, pagination}}
      // apiClient transforms status -> success and wraps the response
      if (response.success && response.data) {
        // response.data contains the API's data object: {items: [], pagination: {}}
        const apiData = response.data;
        const apiItems = apiData.items || [];
        const pagination = apiData.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0
        };

        const listResponse: CategoryListResponse = {
          items: apiItems,
          total: pagination.total,
          page: pagination.page,
          pageSize: pagination.limit,
          totalPages: pagination.totalPages,
        };

        return {
          success: true,
          data: listResponse,
          message: response.message || 'Categories fetched successfully'
        };
      }

      // Return empty response if failed
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: response.message || 'Failed to fetch categories'
      };
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: error?.message || 'Error fetching categories'
      };
    }
  }

  // Get category by ID (GET /admin/categories/{id})
  async getCategoryById(id: number): Promise<ApiResponse<CategoryDetails>> {
    try {
      const response = await apiClient.get<any>(`${this.baseUrl}/${id}`);
      
      if (response.success && response.data) {
        const apiData = response.data;
        return {
          success: true,
          data: apiData,
          message: response.message || 'Category fetched successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to fetch category'
      };
    } catch (error: any) {
      console.error('Error fetching category:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error fetching category'
      };
    }
  }

  // Create category (POST /admin/categories)
  async createCategory(data: CreateCategoryRequest): Promise<ApiResponse<Category>> {
    try {
      const response = await apiClient.post<Category>(this.baseUrl, data);
      if (response.success && response.data) {
        return { success: true, data: response.data, message: response.message || 'Category created successfully' };
      }
      return { success: false, data: undefined, message: response.message || 'Failed to create category' };
    } catch (error: any) {
      console.error('Error creating category:', error);
      return { success: false, data: undefined, message: error?.message || 'Error creating category' };
    }
  }

  // Upload category image (POST /admin/categories/upload-image)
  async uploadImage(file: File, onProgress?: (pct: number) => void): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.upload<{ url: string }>(`${this.baseUrl}/upload-image`, formData, onProgress);
  }

  // Update category (PUT /admin/categories/{id})
  async updateCategory(id: number, data: UpdateCategoryRequest): Promise<ApiResponse<Category>> {
    try {
      const response = await apiClient.put<Category>(`${this.baseUrl}/${id}`, data);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Category updated successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to update category'
      };
    } catch (error: any) {
      console.error('Error updating category:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error updating category'
      };
    }
  }

  // Delete category (DELETE /admin/categories/{id})
  async deleteCategory(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error: any) {
      console.error('Error deleting category:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error deleting category'
      };
    }
  }

  // Get category statistics (calculated from categories list)
  async getCategoryStats(): Promise<ApiResponse<{
    total: number;
    active: number;
    inactive: number;
  }>> {
    try {
      const response = await this.getCategories({ page: 1, limit: 1000 }); // Get all categories
      if (response.success && response.data) {
        const items = response.data.items;
        const stats = {
          total: items.length,
          active: items.filter(c => c.is_active).length,
          inactive: items.filter(c => !c.is_active).length,
        };
        return { success: true, data: stats, message: 'Stats calculated' };
      }
      throw new Error('Failed to fetch categories for stats');
    } catch (error) {
      // Return default stats if calculation fails
      return {
        success: true,
        data: { total: 0, active: 0, inactive: 0 },
        message: 'Default stats'
      };
    }
  }
}

// Export singleton instance
export const categoryService = new CategoryService();
export default categoryService;

